import * as Yup from 'yup'
import { ChainProtocol, EligibilityTokenDataRow, OperationType } from '@archax/shared-types'
import { Button, ButtonGroup, Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import Dialog from '../../../components/Dialog/Dialog'
import onApiError from '../../../util/on-api-error'
import { createOperationForPayoutContract } from '../../../api/operations'
import { getAvailableEligibilityTokensByProtocol } from '../../../api/payouts'
import { Select } from '../../../components/ui/Select'

type SetEligibilityTokenProps = {
  payoutContractId: string
  eligibiltityTokenId: string
  protocol: ChainProtocol
  onClose: () => void
  onConfirm: () => void
}
export default function SetEligibilityToken({
  payoutContractId,
  eligibiltityTokenId,
  protocol,
  onClose,
}: SetEligibilityTokenProps) {
  const { t } = useTranslation()

  const { data: availableEligibilityTokensData, refetch: refetchAvailableEligibilityTokens } = useQuery(
    [`get-available-eligibility-tokens-${protocol}`],
    () =>
      (async () => {
        const response = await getAvailableEligibilityTokensByProtocol(protocol)
        return response.data
      })(),
    {
      retry: false,
      onError: onApiError,
      cacheTime: 0,
    },
  )

  const { mutate: setEligibilityTokenMutation } = useMutation(
    (data: { tokenId: string }) =>
      createOperationForPayoutContract(
        OperationType.PayoutContractSetEligibilityToken,
        { tokenId: data.tokenId },
        { payoutContractId },
      ),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.setEligibilityToken.successMessage'))
      },
      onError: onApiError,
    },
  )

  const validationSchema = Yup.object()
    .shape({
      tokenId: Yup.string().required(),
    })
    .required()

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<{ tokenId: string }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = (data: { tokenId: string }) => {
    setEligibilityTokenMutation(data)
    onClose()
  }

  return (
    <Dialog title={t('payoutContracts.showDetails.actions.setEligibilityToken.message')} open={true} onClose={onClose}>
      <Grid minWidth={540}>
        <form data-testid="set-eligilibility-token" onSubmit={handleSubmit(onSubmit)}>
          <Select
            fullWidth
            data-testid="set-eligilibility-token__input"
            id="tokenId"
            name="tokenId"
            label={t('payoutContracts.showDetails.actions.setEligibilityToken.input')}
            control={control}
            options={(availableEligibilityTokensData?.data || [])
              .filter((it: EligibilityTokenDataRow) => it.id !== eligibiltityTokenId)
              .map((it: EligibilityTokenDataRow) => {
                return {
                  value: it.id,
                  label: `${it.name} (${it.symbol})`,
                }
              })}
          />

          <ButtonGroup fullWidth>
            <Button
              data-testid="set-eligilibility-token__submit-button"
              disabled={!isValid || isSubmitting}
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              fullWidth
            >
              {t('payoutContracts.showDetails.actions.setEligibilityToken.button')}
            </Button>
          </ButtonGroup>
        </form>
      </Grid>
    </Dialog>
  )
}
